import { Link } from "react-router-dom";
import { useUser } from "../../context/userContext";
import axios from "axios";
import { ReactComponent as CloseIcon } from "../../assets/icons/close_icon.svg";
import { useState } from "react";

const PricingBox = (props) => {
  const { userData, isContentLoading, setIsContentLoading } = useUser();
  const {
    className,
    subscription_type,
    subscription_frequency,
    price,
    duration,
    packageName,
    subtitle,
    children,
    setIsPaymentSuccessDialogOpen,
    disabled,
  } = props;

  const [priceBreakupDialogOpen, setPriceBreakupDialogOpen] = useState(false);
  const [companyName, setCompanyName] = useState(null);
  const [tag, setTag] = useState(null);

  const handlePaymentClick = () => {
    setPriceBreakupDialogOpen(true);
  };

  const handleCheckoutClick = async (e) => {
    e.preventDefault();

    if (companyName === "") {
      alert("Company name is mandatory!");
      return;
    }
    if (tag === "") {
      alert("Please provide a tag!");
      return;
    }
    setPriceBreakupDialogOpen(false);

    const API_URL = "https://soliboat.solidaridadasia.com/";

    setIsContentLoading(true);

    try {
      // Step 0: Call the Subscription API
      const subscriptionResponse = await axios.post(
        `${API_URL}subscription`,
        {
          email: userData.email,
          subscription_type: subscription_type,
          daily_subscription: false,
          add_on: false,
          company: companyName,
          tag: tag,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.token}`,
          },
          withCredentials: true,
        }
      );

      console.log("Subscription Response:", subscriptionResponse.data);

      if (subscriptionResponse.data.success) {
        const baseAmount = price;
        const taxRate = 0.18; // 18% tax rate
        const taxInclusiveAmount = baseAmount + baseAmount * taxRate;

        // Step 1: Create an order on server
        const orderResponse = await axios.post(
          `${API_URL}rp_create_orderid`,
          {
            base_amount: baseAmount,
            tax_inclusive_amount: taxInclusiveAmount,
            email: userData.email,
            subscription_frequency: subscription_frequency,
            subscription_type: subscription_type,
            daily_subscription: false,
            add_on: false,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userData.token}`,
            },
            withCredentials: true,
          }
        );

        setIsContentLoading(false);

        const orderData = orderResponse.data.data[0].order_details;
        console.log("Order Data:", orderData);

        // Step 2: Initialize Razorpay
        const options = {
          key: process.env.RAZOR_PAY_KEY_ID,
          name: "XplorEase",
          description: `${userData?.first_name + " : " + packageName}`,
          order_id: orderData.id,
          handler: async (response) => {
            try {
              console.log(response);
              // Step 3: Capture the payment on server
              const paymentId = response.razorpay_payment_id;
              const orderID = response.razorpay_order_id;
              const captureResponse = await axios.post(
                `${API_URL}rp_payment_capture`,
                {
                  email: userData.email,
                  payment_id: paymentId,
                  amount: orderData.amount,
                  order_id: orderID,
                  subscription_frequency: subscription_frequency,
                  subscription_type: subscription_type,
                  daily_subscription: false,
                  add_on: false,
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${userData?.token}`,
                  },
                  withCredentials: true,
                }
              );
              console.log("Capture Response:", captureResponse.data);
              setIsPaymentSuccessDialogOpen(true);
            } catch (captureError) {
              console.error("Error capturing payment:", captureError);
            }
          },
          theme: {
            color: "#3559E0",
          },
        };

        const rzp1 = new window.Razorpay(options);
        rzp1.open();
      } else {
        console.error(
          "Subscription API failed:",
          subscriptionResponse.data.message
        );
      }
    } catch (error) {
      console.error("Error during checkout:", error);
    } finally {
      setCompanyName(null);
      setTag(null);
    }
  };

  return (
    <>
      {/* Initial Loader Screen */}
      {isContentLoading && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-neutral-50 z-[100]">
          <div className="w-20 h-20 animate-spin">
            <div className="h-full w-full border-4 border-t-dark/50 border-b-dark/80 rounded-[50%]" />
          </div>
          <div className="text-center text-sm lg:text-xl font-medium text-gray-500 mt-8 mx-8 lg:mx-0">
            Please wait while while we process your request{" "}
            <span className="text-xl mx-1 inline-block animate-typing">.</span>
            <span className="text-xl mx-1 inline-block animate-typing">.</span>
            <span className="text-xl mx-1 inline-block animate-typing">.</span>
          </div>
        </div>
      )}

      {priceBreakupDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center drop-shadow-[0_4px_24px_rgba(0,0,0,0.24)] z-50">
          <div className="fixed inset-0 bg-black opacity-10" />

          <div className="relative flex flex-col p-4 md:p-6 w-[90%] md:w-[420px] bg-white rounded-md">
            <div className="absolute top-3 right-3 flex rounded-full cursor-pointer hover:scale-[105%]">
              <CloseIcon
                className="w-7 h-7"
                onClick={() => setPriceBreakupDialogOpen(false)}
              />
            </div>
            <div className="flex flex-col mx-2 md:mx-4">
              {subscription_type === "business" && (
                <>
                  <h2 className="mt-2 text-gray-900 text-xl font-semibold">
                    Company Details
                  </h2>
                  <div className="my-2 border-b border-neutral-200" />
                  <div className="w-full mt-2 mb-2 flex items-center">
                    <span className="w-1/3 bg-gray-200 px-3 py-2 rounded-l-md text-gray-600 border border-neutral-200">
                      Company <span className="text-red">*</span>
                    </span>
                    <input
                      type="text"
                      name="companyName"
                      placeholder="Enter your Company"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="w-2/3 py-2 px-3 text-base text-body-color rounded-r-md border border-neutral-200 placeholder-body-color outline-none focus:border-primary"
                    />
                  </div>
                  <div className="w-full mb-2 flex items-center">
                    <span className="w-1/4 bg-gray-200 px-3 py-2 rounded-l-md text-gray-600 border border-neutral-200">
                      Tag <span className="text-red">*</span>
                    </span>
                    <input
                      type="text"
                      name="tag"
                      placeholder="Enter a Tag, e.g: AI"
                      value={tag}
                      onChange={(e) => setTag(e.target.value)}
                      className="w-3/4 py-2 px-3 text-base text-body-color rounded-r-md border border-neutral-200 placeholder-body-color outline-none focus:border-primary"
                    />
                  </div>
                  <div className="my-2 border-b border-neutral-200" />
                </>
              )}
              <h2 className="mt-2 text-gray-900 text-xl font-semibold">
                Order Summary
              </h2>
              <div className="my-2 border-b border-neutral-200" />
              <div className="mt-2 mb-2 flex justify-between text-gray-500">
                <p className="font-medium">Base amount:</p>
                <p className="font-semibold">₹{price}.00</p>
              </div>
              <div className="mb-2 flex justify-between text-gray-500">
                <p className="font-medium">GST (18%):</p>
                <p className="font-semibold">₹{price * 0.18}</p>
              </div>
              <div className="my-2 border-b border-neutral-200" />
              <div className="my-2 flex items-center justify-between text-gray-500">
                <p className="font-medium">Total:</p>
                <p className="text-black font-bold">₹{price + price * 0.18}</p>
              </div>
            </div>
            <button
              className="flex justify-center items-center bg-primary text-white m-2 md:m-4 px-2 py-2 rounded"
              onClick={handleCheckoutClick}
            >
              Pay Now
            </button>
          </div>
        </div>
      )}

      <div
        className={`wow fadeInUp relative z-10 rounded-md bg-white px-8 py-10 xl:px-4 xl:py-8 shadow-signUp xl:rounded-none xl:shadow-none dark:bg-[#1D2144] ${className}`}
        data-wow-delay=".1s"
      >
        <div className="flex xl:flex-col items-center justify-between">
          <h4 className="hidden xl:block mb-2 text-xl font-bold text-dark dark:text-white">
            {packageName}
          </h4>
          <h3 className="price mb-2 text-3xl font-bold text-black dark:text-white">
            <span className="amount">{packageName !== "Enterprise" && "₹"}{price}</span>
            {packageName !== "Enterprise" && (
              <span className="text-xs font-medium text-gray-400">+ GST</span>
            )}
            <span className="time text-body-color">/{duration}</span>
          </h3>
          <h4 className="xl:hidden mb-2 text-xl font-bold text-dark dark:text-white">
            {packageName}
          </h4>
        </div>
        <p className="mb-7 text-base lg:text-center text-body-color">
          {subtitle}
        </p>
        {userData?.email ? (
          <div className="w-full flex justify-center items-center mb-8 border-b border-body-color border-opacity-40 pb-8 dark:border-white dark:border-opacity-10">
            {packageName === "Enterprise" ? (
              <Link
                className={`flex w-full xl:w-4/5 items-center justify-center rounded-md bg-primary p-3 text-base font-semibold text-white transition duration-300 ease-in-out hover:bg-opacity-80 hover:shadow-signUp ${
                  disabled ? "cursor-not-allowed" : ""
                }`}
                to="/support"
                onClick={() => window.scrollTo({ top: 0 })}
                title={
                  disabled &&
                  `You are already on a ${userData.subscription_type} tier.`
                }
              >
                Contact Sales
              </Link>
            ) : (
              <button
                className={`flex w-full xl:w-4/5 items-center justify-center rounded-md bg-primary p-3 text-base font-semibold text-white transition duration-300 ease-in-out hover:bg-opacity-80 hover:shadow-signUp ${
                  disabled ? "cursor-not-allowed" : ""
                }`}
                onClick={handlePaymentClick}
                disabled={disabled}
                title={
                  disabled &&
                  `You are already on a ${userData.subscription_type} tier.`
                }
              >
                Subscribe
              </button>
            )}
          </div>
        ) : (
          <div className="w-full flex justify-center items-center mb-8 border-b border-body-color border-opacity-50 pb-8 dark:border-white dark:border-opacity-10">
            <Link
              to="/signup"
              className="flex w-full xl:w-4/5 items-center justify-center rounded-md bg-primary p-3 text-base font-semibold text-white transition duration-300 ease-in-out hover:bg-opacity-80 hover:shadow-signUp"
            >
              Start Free Trial
            </Link>
          </div>
        )}
        <div>{children}</div>

        <div className="absolute bottom-0 right-0 z-[-1]">
          <svg
            width="179"
            height="158"
            viewBox="0 0 179 158"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              opacity="0.5"
              d="M75.0002 63.256C115.229 82.3657 136.011 137.496 141.374 162.673C150.063 203.47 207.217 197.755 202.419 167.738C195.393 123.781 137.273 90.3579 75.0002 63.256Z"
              fill="url(#paint0_linear_70:153)"
            />
            <path
              opacity="0.3"
              d="M178.255 0.150879C129.388 56.5969 134.648 155.224 143.387 197.482C157.547 265.958 65.9705 295.709 53.1024 246.401C34.2588 174.197 100.939 83.7223 178.255 0.150879Z"
              fill="url(#paint1_linear_70:153)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_70:153"
                x1="69.6694"
                y1="29.9033"
                x2="196.108"
                y2="83.2919"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#4A6CF7" stopOpacity="0.62" />
                <stop offset="1" stopColor="#4A6CF7" stopOpacity="0" />
              </linearGradient>
              <linearGradient
                id="paint1_linear_70:153"
                x1="165.348"
                y1="-75.4466"
                x2="-3.75136"
                y2="103.645"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#4A6CF7" stopOpacity="0.62" />
                <stop offset="1" stopColor="#4A6CF7" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>
    </>
  );
};

export default PricingBox;
